import { Autocomplete, Box, InputAdornment, TextField, Typography } from '@mui/material'
import React, { FC, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { TState } from 'redux/config/root-types'
import useDebounce from 'ui/hooks/useDebounce'
import { ExternalLink, Search } from 'lucide-react'
import { RequestMethods } from 'utils/axios/methods'
import { TUserSearch } from 'utils/types/TUserSearch'

type Props = {
  user: TUserSearch | null
  onChangeUser: (user: TUserSearch | null) => void
}

export const SearchUser: FC<Props> = ({ user, onChangeUser }) => {
  const dispatch = useDispatch()
  const filters = useSelector((state: TState) => state.bookings.filters)

  const [inputValue, setInputValue] = useState(filters.searchStr || '')
  // const [value, setValue] = useState<TLocation | null>(null)
  const [options, setOptions] = useState<TUserSearch[]>([])
  const [loading, setLoading] = useState(false)

  const debouncedInput = useDebounce(inputValue, 1000)

  useEffect(() => {
    if (debouncedInput.trim().length < 2) {
      setOptions([])
      setLoading(false)
      return
    }

    const fetchCities = async () => {
      setLoading(true)
      try {
        const results = await searchCities(debouncedInput)
        setOptions(results)
      } catch (error) {
        console.error('Failed to fetch cities:', error)
        setOptions([])
      } finally {
        setLoading(false)
      }
    }

    fetchCities()
  }, [debouncedInput])

  const navigateToUser = () => {
    const url = `/users/details/${user?.entityId}`
    window.open(url, '_blank', 'noopener,noreferrer') // безопасно и без доступа к window.opener
  }

  return (
    <div>
      <Typography variant="body2" sx={{ color: '#6B7280', marginTop: '1rem' }}>
        User
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: '1rem', alignItems: 'center' }}>
        <Autocomplete
          freeSolo
          options={options}
          getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
          value={user}
          inputValue={inputValue}
          filterOptions={(x) => x}
          onInputChange={(_, newInputValue) => {
            setInputValue(newInputValue)
          }}
          onChange={(_, newValue) => {
            if (typeof newValue === 'string') {
              onChangeUser(null)
              setInputValue(newValue)
            } else if (newValue === null) {
              onChangeUser(null)
              setInputValue('')
            } else {
              onChangeUser(newValue)
              setInputValue(newValue.name)
            }
          }}
          sx={{ width: '100%' }}
          loading={loading}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Search"
              inputProps={{
                ...params.inputProps,
                type: 'text',
                autoComplete: 'off',
                autoCorrect: 'off',
                spellCheck: 'false',
                readOnly: false,
              }}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start" sx={{ mt: 0, pl: '0.5rem' }}>
                    <Search size={'1.25rem'} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: '100%',
                marginTop: 0,
                backgroundColor: '#FFF',
                borderRadius: 1,
                '& .MuiOutlinedInput-root': {
                  height: '3rem',
                  borderRadius: 1,
                  display: 'flex',
                  padding: 0,
                  alignItems: 'center',
                  '& fieldset': {
                    borderColor: '#E5E7EB',
                  },
                  '&:hover fieldset': {
                    borderColor: '#E5E7EB',
                  },
                  '&.Mui-focused fieldset': {
                    borderWidth: 1,
                    borderColor: '#E5E7EB',
                  },
                },
                '& .MuiInputBase-input': {
                  fontSize: '1.125rem',
                  padding: '1rem 0',
                  lineHeight: 'normal',
                },
              }}
            />
          )}
        />
        <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }} onClick={() => navigateToUser()}>
          <ExternalLink size={'1.25rem'} color={user ? '#F97316' : '#E5E7EB'} />
        </div>
      </Box>
    </div>
  )
}

const searchCities = async (query: string): Promise<TUserSearch[]> => {
  const { get } = RequestMethods()
  const res = await get(`/search/?searchStr=${encodeURIComponent(query)}&language=en&entity=user`)
  return res.data as TUserSearch[]
}
